import { useIntl } from '@capsicum/common';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export type UkSelfCategorisationHighNetWorthInvestorFormType = z.infer<
	ReturnType<typeof useUkSelfCategorisationHighNetWorthInvestorSchema>
>;

const YesOrNoOptions = {
	NO: false,
	YES: true
} as const;

const useUkSelfCategorisationHighNetWorthInvestorSchema = () => {
	const { $t } = useIntl();

	return z.object({
		highActualAnnualIncome: z
			.string()
			.min(1, $t('requirements.common.form.errors.emptyValue'))
			.refine(
				val => {
					const regex = /^(0|[1-9][0-9]*)$/;

					return regex.test(val);
				},
				{
					message: $t('requirements.common.form.errors.notValidNumber')
				}
			)
			.refine(
				val => {
					return parseInt(val) >= 0;
				},
				{
					message: $t('requirements.common.form.errors.notValidNumber')
				}
			)
			.refine(
				val => {
					return parseInt(val) <= 999999999;
				},
				{
					message: $t('requirements.common.form.errors.maxValueReached', {
						value: '999.999.999'
					})
				}
			),
		highActualNetAssets: z
			.string()
			.min(1, $t('requirements.common.form.errors.emptyValue'))
			.refine(
				val => {
					const regex = /^(0|[1-9][0-9]*)$/;

					return regex.test(val);
				},
				{
					message: $t('requirements.common.form.errors.notValidNumber')
				}
			)
			.refine(
				val => {
					return parseInt(val) >= 0;
				},
				{
					message: $t('requirements.common.form.errors.notValidNumber')
				}
			)
			.refine(
				val => {
					return parseInt(val) <= 999999999;
				},
				{
					message: $t('requirements.common.form.errors.maxValueReached', {
						value: '999.999.999'
					})
				}
			)
	});
};

const useUkSelfCategorisationHighNetWorthInvestorForm = () => {
	const ukSelfCategorisationHighNetWorthInvestorForm = useForm<UkSelfCategorisationHighNetWorthInvestorFormType>({
		defaultValues: useMemo(
			() => ({
				highActualAnnualIncome: '',
				highActualNetAssets: ''
			}),
			[]
		),
		mode: 'onChange',
		resolver: zodResolver(useUkSelfCategorisationHighNetWorthInvestorSchema())
	});

	return ukSelfCategorisationHighNetWorthInvestorForm;
};

export const useUkSelfCategorisationHighNetWorthInvestor = () => {
	const [reset, setReset] = useState<boolean | null>(null);
	const [highAnnualIncomeChoice, setHighAnnualIncomeChoice] = useState<boolean | null>(null);
	const [netAssetsChoice, setNetAssetsChoice] = useState<boolean | null>(null);

	const ukSelfCategorisationHighNetWorthInvestorForm = useUkSelfCategorisationHighNetWorthInvestorForm();
	const { $t } = useIntl();

	const resetOptions = [
		{
			id: YesOrNoOptions.YES,
			label: $t('jurisdictionRequirements.ukSelfCategorisation.optionYes')
		}
	];

	const highAnnualIncomeChoiceOptions = [
		{
			id: YesOrNoOptions.NO,
			label: $t('jurisdictionRequirements.ukSelfCategorisation.optionNo')
		},
		{
			id: YesOrNoOptions.YES,
			label: $t('jurisdictionRequirements.ukSelfCategorisation.highNetWorthHighAnnualIncomeChoiceOptionsYes')
		}
	];

	const netAssetsChoiceOptions = [
		{
			id: YesOrNoOptions.NO,
			label: $t('jurisdictionRequirements.ukSelfCategorisation.optionNo')
		},
		{
			id: YesOrNoOptions.YES,
			label: $t('jurisdictionRequirements.ukSelfCategorisation.highNetWorthHighAnnualIncomeChoiceOptionsYes')
		}
	];

	const highActualIncomeValue = ukSelfCategorisationHighNetWorthInvestorForm.watch('highActualAnnualIncome');
	const netAssetsValue = ukSelfCategorisationHighNetWorthInvestorForm.watch('highActualNetAssets');

	const isHighActualIncomeValueValid =
		!ukSelfCategorisationHighNetWorthInvestorForm.formState.errors.highActualAnnualIncome;
	const isNetAssetsValueValid = !ukSelfCategorisationHighNetWorthInvestorForm.formState.errors.highActualNetAssets;

	const setUserHighActualAnnualIncome = (value: boolean) => {
		if (!value) {
			ukSelfCategorisationHighNetWorthInvestorForm.clearErrors('highActualAnnualIncome');
			ukSelfCategorisationHighNetWorthInvestorForm.reset({
				highActualAnnualIncome: undefined
			});
		}
		setHighAnnualIncomeChoice(value);
		setReset(null);
	};

	const setUserNetAssets = useCallback(
		(value: boolean) => {
			if (!value) {
				ukSelfCategorisationHighNetWorthInvestorForm.setValue('highActualNetAssets', '');
				ukSelfCategorisationHighNetWorthInvestorForm.clearErrors('highActualNetAssets');
			}

			setNetAssetsChoice(value);
			setReset(null);
		},
		[ukSelfCategorisationHighNetWorthInvestorForm]
	);

	const resetHighNetWorthChoice = useCallback((value: boolean) => {
		setReset(value);
		setNetAssetsChoice(null);
		ukSelfCategorisationHighNetWorthInvestorForm.clearErrors('highActualAnnualIncome');
		ukSelfCategorisationHighNetWorthInvestorForm.reset({
			highActualAnnualIncome: undefined
		});

		setHighAnnualIncomeChoice(null);
		ukSelfCategorisationHighNetWorthInvestorForm.setValue('highActualNetAssets', '');
		ukSelfCategorisationHighNetWorthInvestorForm.clearErrors('highActualNetAssets');
	}, []);

	const isHighNetWorthFormValid = useMemo(() => {
		const isHighAnnualIncomeChoiceValid =
			highAnnualIncomeChoice === false || (highActualIncomeValue !== '' && isHighActualIncomeValueValid);

		const isHighNetAssetsChoiceValid =
			netAssetsChoice === false || (netAssetsValue !== '' && isNetAssetsValueValid !== false);

		return reset === true || (isHighAnnualIncomeChoiceValid && isHighNetAssetsChoiceValid);
	}, [
		highAnnualIncomeChoice,
		highActualIncomeValue,
		isHighActualIncomeValueValid,
		netAssetsChoice,
		netAssetsValue,
		isNetAssetsValueValid,
		reset
	]);

	return {
		highActualIncomeValue,
		highAnnualIncomeChoice,
		highAnnualIncomeChoiceOptions,
		isHighNetWorthFormValid,
		netAssetsChoice,
		netAssetsChoiceOptions,
		netAssetsValue,
		reset,
		resetHighNetWorthChoice,
		resetOptions,
		setNetAssetsChoice,
		setUserHighActualAnnualIncome,
		setUserNetAssets,
		ukSelfCategorisationHighNetWorthInvestorForm
	};
};
